import { useEffect } from 'react';
import { useThrottleFn } from 'ahooks';
import { any, isNil, isNotNil } from 'ramda';
import { lengthGt } from 'ramda-adjunct';

// Selectors targeting Ant Design 4.x (antd@^4.24.16) popups.
// These class names are internal to AntD 4 and should be revisited on a v5 migration.
const OPEN_SELECT_SELECTOR = '.ant-select-dropdown:not(.ant-select-dropdown-hidden)';
const OPEN_DROPDOWN_SELECTOR = '.ant-dropdown:not(.ant-dropdown-hidden)';

// Returns true when the scrolled node belongs to an open popup (its own internal scroll).
const isScrollInsidePopup = (target, openPopups) => {
  if (isNil(target) || target === document || target === window) {
    return false;
  }
  // AntD renders popups in a portal on document.body, so we cannot rely on
  // target.closest() alone: we also check whether an open popup contains the target.
  return any((popup) => popup === target || popup.contains(target), openPopups);
};

export const useClosePopupsOnScroll = () => {
  const { run: handleScroll } = useThrottleFn(
    (e) => {
      const openSelects = Array.from(document.querySelectorAll(OPEN_SELECT_SELECTOR));
      const openDropdowns = Array.from(document.querySelectorAll(OPEN_DROPDOWN_SELECTOR));

      // Short-circuit: nothing to close, avoid any further DOM work.
      if (!lengthGt(0, openSelects) && !lengthGt(0, openDropdowns)) {
        return;
      }

      // Ignore scroll happening inside an open popup (e.g. long dropdown list).
      if (isScrollInsidePopup(e.target, [...openSelects, ...openDropdowns])) {
        return;
      }

      // Select: close it natively by blurring the focused input. No synthetic
      // event is dispatched, so no other listener can be affected.
      const { activeElement } = document;
      if (lengthGt(0, openSelects) && isNotNil(activeElement) && activeElement.closest('.ant-select')) {
        activeElement.blur();
      }

      // Dropdown: rc-trigger only closes on an outside `mousedown` reaching
      // `document`, and there is no native scroll-close for standard dropdowns
      // in AntD 4. We therefore dispatch a synthetic `mousedown` ONLY when a
      // dropdown is actually open. This is safe against existing listeners:
      // - react-dnd HTML5Backend listens on `dragstart`/`drop`, not `mousedown`.
      // - DropZone's useClickAway listens on `click`, not `mousedown`.
      if (lengthGt(0, openDropdowns)) {
        const mousedownEvent = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(mousedownEvent);
      }
    },
    {
      wait: 100,
      leading: true,
      trailing: true
    }
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);
};
